import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { IFilter, IFilterValue, IRequestValues, SortOrder, toApiSortBy } from "../../components/UI/Declarations";
import { useLocalStorage } from "../../components/UI/LocalStorage";
import {
    buildPagedListFilterData,
    FilterMigration,
    mergePersistedFilters,
    readPersistedFilterValues,
    ResolveStaticFilterValues,
} from "../../utils/helpers/pagedListHelpers";

interface UsePagedListFiltersProps {
    filters: IFilter[];
    staticFilterValues: Record<string, any>;
    resolveStaticFilterValues?: ResolveStaticFilterValues;
    persistanceKey?: string;
    filterMigrations?: Record<string, FilterMigration>;
}

export const usePagedListFilters = ({
    filters,
    staticFilterValues,
    resolveStaticFilterValues,
    persistanceKey,
    filterMigrations,
}: UsePagedListFiltersProps) => {
    const filterMembersKey = filters.map((f) => f.member).join("|");
    const reconciledKeyRef = useRef(filterMembersKey);

    const [filterValues, setFilterValues] = useState<IFilterValue[]>(() =>
        mergePersistedFilters(filters, readPersistedFilterValues(persistanceKey, filterMigrations))
    );
    const [lastRequestValues, setLastRequestValues] = useState<IRequestValues | null>(null);

    const [, setStoredFilters] = useLocalStorage<IFilterValue[]>(persistanceKey + "-filters", null);

    const filtersReady = reconciledKeyRef.current === filterMembersKey;

    useLayoutEffect(() => {
        if (reconciledKeyRef.current === filterMembersKey) {
            return;
        }
        const stored = readPersistedFilterValues(persistanceKey, filterMigrations);
        setFilterValues(mergePersistedFilters(filters, stored));
        reconciledKeyRef.current = filterMembersKey;
    }, [filterMembersKey, filters, persistanceKey, filterMigrations]);

    const filterChanged = useCallback((last: Record<string, any>, current: Record<string, any>): boolean => {
        for (const key in current) {
            if (last[key] !== current[key]) {
                return true;
            }
        }
        return false;
    }, []);

    const requestParamsChanged = useCallback((last: IRequestValues, current: IRequestValues): boolean => {
        return last.offset !== current.offset ||
               last.count !== current.count ||
               last.sortBy !== current.sortBy ||
               last.sortOrder !== current.sortOrder ||
               filterChanged(last.filter, current.filter);
    }, [filterChanged]);

    const handleFilterValueChanged = useCallback((filterValues: IFilterValue[]): void => {
        setFilterValues(filterValues);
        setStoredFilters(filterValues);
    }, [setStoredFilters]);

    const handleFilterReset = useCallback((): void => {
        const resetFilters = filters.map((filter) => {
            return { filter: filter, value: filter.defaultValue ?? null } as IFilterValue;
        });
        setFilterValues(resetFilters);
        setStoredFilters(resetFilters);
    }, [filters, setStoredFilters]);

    const buildRequestValues = useCallback((offset: number, count: number, sortBy: string, sortOrder: SortOrder): IRequestValues => {
        const filterData = buildPagedListFilterData(
            filterValues,
            staticFilterValues,
            resolveStaticFilterValues
        );

        return {
            filter: filterData,
            offset: offset,
            count: count,
            sortBy: toApiSortBy(sortBy),
            sortOrder: sortOrder,
        };
    }, [filterValues, staticFilterValues, resolveStaticFilterValues]);

    return {
        filterValues,
        setFilterValues,
        lastRequestValues,
        setLastRequestValues,
        filterChanged,
        requestParamsChanged,
        handleFilterValueChanged,
        handleFilterReset,
        buildRequestValues,
        filtersReady,
    };
};
